/**
 * Numbers for the stats pages, in the reader's language. `Intl` knows the
 * unit words and their plurals in every language the app ships, so none of
 * this needs a translation key.
 */
function unit(language: string, value: number, name: string, long = false): string {
	try {
		return new Intl.NumberFormat(language, {
			style: 'unit',
			unit: name,
			unitDisplay: long ? 'long' : 'short',
			maximumFractionDigits: 0,
		}).format(value);
	} catch {
		// No unit formatting in this browser: the international symbol, not
		// an English word.
		return `${value} ${SYMBOLS[name] ?? name}`;
	}
}

const SYMBOLS: Record<string, string> = {
	second: 's',
	minute: 'min',
	hour: 'h',
	day: 'd',
};

/** `42 sec`, `3 min`, `1 hr 2 min`: coarse on purpose, this is not a stopwatch. */
export function formatReadingTime(ms: number, language: string): string {
	const seconds = Math.max(0, Math.round(ms / 1000));
	if (seconds < 60) {
		return unit(language, seconds, 'second');
	}
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) {
		return unit(language, minutes, 'minute');
	}
	const rest = minutes % 60;
	const hours = unit(language, Math.floor(minutes / 60), 'hour');
	return rest ? `${hours} ${unit(language, rest, 'minute')}` : hours;
}

export function formatDays(days: number, language: string): string {
	return unit(language, days, 'day', true);
}

export function formatPercent(fraction: number, language: string): string {
	const clamped = Math.min(1, Math.max(0, fraction));
	try {
		return new Intl.NumberFormat(language, {
			style: 'percent',
			maximumFractionDigits: 0,
		}).format(clamped);
	} catch {
		return `${Math.round(clamped * 100)}%`;
	}
}

export function formatCount(value: number, language: string): string {
	try {
		return new Intl.NumberFormat(language).format(value);
	} catch {
		return String(value);
	}
}

export function formatDate(timestamp: number, language: string): string {
	try {
		return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(
			timestamp,
		);
	} catch {
		return new Date(timestamp).toDateString();
	}
}

/** `Mon`, `Tue`: a day's name, short enough to sit under a chart's column. */
export function formatWeekday(timestamp: number, language: string): string {
	try {
		return new Intl.DateTimeFormat(language, { weekday: 'short' }).format(
			timestamp,
		);
	} catch {
		return new Date(timestamp).toDateString().slice(0, 3);
	}
}

/** `Monday, Oct 5`: a day named in full, as a heading. */
export function formatDay(timestamp: number, language: string): string {
	try {
		return new Intl.DateTimeFormat(language, {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
		}).format(timestamp);
	} catch {
		return new Date(timestamp).toDateString();
	}
}

/** `7`: the day of the month, for a chart's axis over many days. */
export function formatDayOfMonth(timestamp: number, language: string): string {
	try {
		return new Intl.DateTimeFormat(language, { day: 'numeric' }).format(timestamp);
	} catch {
		return String(new Date(timestamp).getDate());
	}
}

/** `2 PM` or `14`, as the language tells the time. */
export function formatHour(timestamp: number, language: string): string {
	try {
		return new Intl.DateTimeFormat(language, { hour: 'numeric' }).format(timestamp);
	} catch {
		return String(new Date(timestamp).getHours());
	}
}

/** `2 – 3 PM`: the hour starting at `timestamp`, as a heading. */
export function formatHourRange(timestamp: number, language: string): string {
	const end = timestamp + 60 * 60 * 1000;
	try {
		return new Intl.DateTimeFormat(language, { hour: 'numeric' }).formatRange(
			timestamp,
			end,
		);
	} catch {
		return `${formatHour(timestamp, language)} – ${formatHour(end, language)}`;
	}
}
